import React from 'react';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';

import {
  accessHotelMetadataUsingHotelIds,
  getCurrencyData,
  getLocations,
  getHotelsV5,
  getPopularHotels,
} from '@utils/services';
import Heading, { type HeadingProps } from '@components/DynamicLandingPages/pure/Heading';
import PartnerLogo from '@components/DynamicLandingPages/pure/PartnerLogo';
import InfoSection from '@components/DynamicLandingPages/pure/InfoSection';
import HotelSection from '@components/DynamicLandingPages/pure/HotelSection';
import SearchbarSection from '@components/DynamicLandingPages/pure/SearchbarSection';
import FavoriteLocationsSection from '@components/DynamicLandingPages/pure/FavoriteLocationsSection';
import TestimonialSection from '@components/DynamicLandingPages/pure/TestimonialSection';
import FaqSection from '@components/DynamicLandingPages/pure/FaqSection';
import LegalSection from '@components/DynamicLandingPages/pure/LegalSection';
import Footer from '@components/Footer/Footer';
// Utils
import { removeDuplicates, sortHotels } from '@components/DynamicLandingPages/utils';
// Types
import type HotelApi from '@customTypes/hotel-api';
import type HotelDetails from '@customTypes/hotel-details';
import { type HotelCardProps } from '@components/DynamicLandingPages/pure/HotelCard';
import { type InfoCardProps } from '@components/DynamicLandingPages/pure/InfoCard';
import { type FavoriteLocation } from '@components/DynamicLandingPages/pure/FavoriteLocationsSection';
import { type FaqItemProps } from '@components/DynamicLandingPages/pure/FaqItem';
import { type TestimonialCardProps } from '@components/DynamicLandingPages/pure/TestimonialCard';
import { type TestimonialSectionProps } from '@components/DynamicLandingPages/pure/TestimonialSection';
import { type HotelApiResponseItem } from '@customTypes/hotel-api-response';

type TestimonialHotels = {
  hotelId: string;
  product: string;
  rating: number;
  description: string;
  clientName: string;
};

type CTA = {
  text: string;
  url: string;
};

type FileProps = {
  tabTitle: string;
  metadata: {
    description: string;
  };
  header: HeadingProps;
  partnerLogo: {
    url: string;
    backgroundUrl: string;
    ctaUrl: string;
  };
  infoSection: InfoCardProps[];
  hotelSection: {
    hotelIds: string[];
    headline: string;
  };
  searchbarSection: {
    backgroundUrl: string;
    applyFilters: string[];
  };
  favoriteLocations: FavoriteLocation[];
  testimonialHotels: {
    testimonials: TestimonialHotels[];
    cta: CTA;
  };
  faqSection: FaqItemProps[];
  legalSection: {
    content: string;
  };
};

type LandingPagesProps = {
  tabTitle: string;
  metadata: {
    description: string;
  };
  header: HeadingProps;
  partnerLogo: {
    url: string;
    backgroundUrl: string;
    ctaUrl: string;
  };
  infoSection: InfoCardProps[];
  hotelSection: {
    hotels: HotelCardProps[];
    headline: string;
  };
  searchbarBackground: string;
  applyFilters: string[];
  favoriteLocations: FavoriteLocation[];
  testimonialSection: TestimonialSectionProps;
  faqSection: FaqItemProps[];
  legalSection: {
    content: string;
  };
};

const getHotels = async (hotelIds: string[]): Promise<HotelCardProps[]> => {
  const hotelsData: HotelDetails[] = await accessHotelMetadataUsingHotelIds(hotelIds, false);
  const sortedHotels = sortHotels(hotelsData, hotelIds);
  return sortedHotels.map((hotel: any) => ({
    imageUrl: hotel.image_desktop,
    name: hotel.name,
    location: `${hotel.city_name}, ${hotel.state_name}`,
    rating: parseFloat(hotel?.avg_rating) || 0,
    reviews: hotel?.reviews_count || 0,
    description: hotel.short_description,
    url: `hotels/${hotel.url}`,
  }));
};

const getNearHotels = async (userCityAndState: string): Promise<HotelApi[] | null> => {
  const response = await getLocations(`"${userCityAndState}"`);
  if (response?.length > 0) {
    const cityId = response[0]?.id;
    if (cityId) {
      const query = `city_id:${cityId}&stage=${1}&page=${0}`;
      const hotelData = await getHotelsV5(query, {});
      if (hotelData && hotelData instanceof Array) {
        const stageOneHotels = (hotelData as HotelApiResponseItem[])[0].hotels;

        if (stageOneHotels.length < 6) {
          const stageTwoHotels = (hotelData as HotelApiResponseItem[])[1].hotels;
          const stageOneAndTwoHotels = [...stageOneHotels, ...stageTwoHotels];

          if (stageOneAndTwoHotels.length < 6) {
            const stageThreeHotels = (hotelData as HotelApiResponseItem[])[2].hotels;
            return removeDuplicates([
              ...stageOneHotels,
              ...stageTwoHotels,
              ...stageThreeHotels,
            ]).slice(0, 6);
          }
          return removeDuplicates(stageOneAndTwoHotels).slice(0, 6);
        }
        return removeDuplicates(stageOneHotels).slice(0, 6);
      }
    }
  }
  return null;
};

const getTestimonialHotels = async (
  testimonials: TestimonialHotels[],
): Promise<TestimonialCardProps[]> => {
  const hotelIds = testimonials.map((testimonial) => testimonial.hotelId);
  const hotelsData: HotelDetails[] = await accessHotelMetadataUsingHotelIds(hotelIds, false);
  const sortedHotels = sortHotels(hotelsData, hotelIds);
  return sortedHotels.map((hotel) => {
    const testimonial = testimonials.find((t) => t.hotelId === hotel.id.toString());
    return {
      imageUrl: hotel.image_desktop,
      hotelName: hotel.name,
      product: testimonial?.product,
      clientName: testimonial?.clientName,
      rating: testimonial?.rating,
      description: testimonial?.description,
      url: `hotels/${hotel.url}`,
    };
  });
};

export const getServerSideProps: GetServerSideProps<LandingPagesProps> = async (context) => {
  const landingPage = context.query['landing-page'];
  const forwarded = context.req.headers['x-forwarded-for'];
  const ip =
    typeof forwarded === 'string' ? forwarded.split(/, /)[0] : context.req.socket.remoteAddress;
  try {
    // Dynamic import JSON file based on route name
    const landingPageData: FileProps = await import(`../landing-pages/${landingPage}.json`);
    const defaultMetadata = {
      description: '',
    };
    let hotelsData: HotelCardProps[];
    let headline: string = '';
    let searchbarBackground: string = '';
    const {
      tabTitle,
      metadata = defaultMetadata,
      header,
      partnerLogo,
      infoSection,
      hotelSection,
      searchbarSection,
      favoriteLocations,
      testimonialHotels,
      faqSection,
      legalSection,
    } = landingPageData;
    if (!hotelSection.hotelIds) {
      const currencyData = await getCurrencyData(ip);
      const userCityAndState = `${currencyData.city}, ${currencyData.state}` ?? '';
      const nearHotelsData = await getNearHotels(userCityAndState);
      if (nearHotelsData) {
        headline = hotelSection.headline || `Get away for the day near ${userCityAndState || ''}`;
        hotelsData = nearHotelsData.map((hotel) => ({
          imageUrl: hotel.imageDesktop,
          name: hotel.name,
          location: `${hotel.cityName}, ${hotel.state}`,
          rating: hotel.avgRating ? parseFloat(hotel.avgRating) : 0,
          reviews: hotel.reviewsCount || 0,
          description: hotel.shortDescription,
          url: `/hotels/${hotel.url}`,
        }));
      } else {
        headline = hotelSection.headline || 'Get away for the day';
        const popularHotels = await getPopularHotels();
        hotelsData = popularHotels.hotels.map((hotel) => ({
          imageUrl: hotel.image_desktop,
          name: hotel.name,
          location: `${hotel.city_name}, ${hotel.state_name}`,
          rating: hotel.avg_rating ? parseFloat(hotel.avg_rating) : 0,
          reviews: hotel.reviews_count || 0,
          description: hotel.short_description,
          url: `/hotels/${hotel.url}`,
        }));
      }
    } else {
      headline = hotelSection.headline;
      hotelsData = await getHotels(hotelSection.hotelIds);
    }

    if (searchbarSection) {
      searchbarBackground = searchbarSection.backgroundUrl;
    }

    const testimonialData: TestimonialCardProps[] = await getTestimonialHotels(
      testimonialHotels.testimonials,
    );
    const testimonialSection = {
      testimonials: testimonialData,
      cta: testimonialHotels.cta,
    };
    const hotelSectionData = {
      hotels: hotelsData,
      headline,
    };
    return {
      props: {
        tabTitle,
        metadata,
        header,
        partnerLogo,
        infoSection,
        hotelSection: hotelSectionData,
        searchbarBackground,
        applyFilters: searchbarSection.applyFilters,
        favoriteLocations,
        testimonialSection,
        faqSection: faqSection || null,
        legalSection: legalSection || null,
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

export default function LandingPage({
  tabTitle,
  metadata,
  header,
  partnerLogo,
  infoSection,
  hotelSection,
  searchbarBackground,
  applyFilters,
  favoriteLocations,
  testimonialSection,
  faqSection,
  legalSection,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <>
      <Head>
        <title>{tabTitle}</title>
        <meta name="description" content={metadata.description} />
      </Head>
      <Heading
        imageUrl={header.imageUrl}
        title={header.title}
        subtitle={header.subtitle}
        cta={header.cta}
      />
      <PartnerLogo
        backgroundUrl={partnerLogo.backgroundUrl}
        imageUrl={partnerLogo.url}
        ctaUrl={partnerLogo.ctaUrl}
      />
      <InfoSection sections={infoSection} />
      <HotelSection headline={hotelSection.headline} hotels={hotelSection.hotels} />
      <SearchbarSection background={searchbarBackground} applyFilters={applyFilters} />
      <FavoriteLocationsSection favoriteLocations={favoriteLocations} />
      <TestimonialSection
        testimonials={testimonialSection.testimonials}
        cta={testimonialSection.cta}
      />
      {(faqSection || legalSection) && (
        <>
          {faqSection && <FaqSection faqData={faqSection} />}
          {legalSection ? (
            <LegalSection legalText={legalSection.content} />
          ) : (
            <div
              id="legal-section"
              className="w-full bg-rp-daylight pt-20 border-b border-rp-primary-redesign"
            />
          )}
        </>
      )}
    </>
  );
}

LandingPage.getLayout = (page: React.ReactNode) => (
  <>
    <div className="bg-rp-daylight-opacity text-rp-neutral-900 font-rp-basetica">{page}</div>
    <Footer />
  </>
);
